import React, { useEffect, useState, useCallback } from "react";
import {
  Container, Row, Col, Card, CardBody, CardHeader,
  Input, Button, Table, Spinner, Alert,
} from "reactstrap";
import { usePostHog } from "@posthog/react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useAuthUrl } from "../utils/authRedirect";
import requests from "../requests";
import { buildDashboardUrl } from "../utils/links";
import { SignalCell, PatternCell, MomentumCell } from "./SignalCells";
import { useSeo } from "../hooks/useSeo";

interface WatchlistItem {
  ticker: string;
  ticker_name: string;
  created_at: string;
  signals?: TickerSignals;
}

export default function Watchlist() {
  useSeo({ title: "Watchlist", path: "/watchlist", noindex: true });
  const authUrlFor = useAuthUrl();
  const posthog = usePostHog();
  const isAuthenticated = useSelector((s: RootReducerState) => s.authentication.loggedIn as boolean);
  const authChecked = useSelector((s: RootReducerState) => s.authentication.authChecked as boolean);
  const [items, setItems] = useState<WatchlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const fetchWatchlist = useCallback(() => {
    if (!isAuthenticated) return;
    setLoading(true);
    requests.get("/api/watchlist")
      .then(res => { setItems(res.data.watchlist || []); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, [isAuthenticated]);

  useEffect(() => { fetchWatchlist(); }, [fetchWatchlist]);
  useEffect(() => {
    posthog.capture("watchlist_viewed");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addTicker = async () => {
    const ticker = input.trim().toUpperCase();
    if (!ticker) return;
    setAdding(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await requests.post("/api/watchlist", { ticker });
      setInput("");
      setSuccessMsg(`${ticker} added to watchlist`);
      posthog.capture("watchlist_ticker_added", { ticker });
      fetchWatchlist();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } } | undefined)?.response?.data?.error;
      setError(msg ?? `Could not add ${ticker}`);
    } finally {
      setAdding(false);
    }
  };

  const removeTicker = async (ticker: string) => {
    try {
      await requests.delete(`/api/watchlist/${ticker}`);
      setItems(prev => prev.filter(i => i.ticker !== ticker));
      posthog.capture("watchlist_ticker_removed", { ticker });
    } catch {
      setError(`Could not remove ${ticker}`);
    }
  };

  if (!authChecked) {
    return <Container className="py-5 text-center"><Spinner color="primary" /></Container>;
  }

  if (!isAuthenticated) {
    return (
      <Container className="py-5 text-center">
        <p className="text-muted">Please <Link to={authUrlFor("/login")}>log in</Link> to use your watchlist.</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <h2 className="mb-1" style={{ fontWeight: 700 }}>My Watchlist</h2>
      <p className="text-muted mb-4" style={{ fontSize: 15 }}>
        Save tickers to scope your feed and alerts to stocks you care about.
      </p>

      {/* Add ticker */}
      <Card className="mb-4">
        <CardBody>
          <Row className="g-2 align-items-center">
            <Col xs={8} md={4}>
              <Input
                placeholder="Enter ticker (e.g. AAPL)"
                value={input}
                onChange={e => setInput(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === "Enter" && void addTicker()}
              />
            </Col>
            <Col xs={4} md={2}>
              <Button color="primary" onClick={() => void addTicker()} disabled={adding || !input.trim()} className="w-100">
                {adding ? <Spinner size="sm" /> : "Add"}
              </Button>
            </Col>
          </Row>
          {error && <Alert color="danger" className="mt-2 mb-0 py-2">{error}</Alert>}
          {successMsg && <Alert color="success" className="mt-2 mb-0 py-2">{successMsg}</Alert>}
        </CardBody>
      </Card>

      {/* Watchlist table */}
      <Card>
        <CardHeader style={{ borderBottom: "1px solid var(--border-color)" }}>
          <span style={{ fontWeight: 600 }}>
            Watching {items.length} ticker{items.length !== 1 ? "s" : ""}
          </span>
        </CardHeader>
        <CardBody className="p-0">
          {loading ? (
            <div className="text-center py-4"><Spinner color="primary" /></div>
          ) : items.length === 0 ? (
            <p className="text-muted text-center py-4 mb-0">No tickers yet — add some above.</p>
          ) : (
            <Table responsive hover className="mb-0">
              <thead>
                <tr className="text-muted" style={{ fontSize: 13 }}>
                  <th>Ticker</th>
                  <th>Name</th>
                  <th>Latest Signal</th>
                  <th>Pattern (7d)</th>
                  <th>Momentum</th>
                  <th>Added</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.ticker}>
                    <td>
                      <Link
                        to={buildDashboardUrl({ ticker: item.ticker })}
                        style={{ fontWeight: 600, color: "var(--primary)" }}
                      >
                        {item.ticker}
                      </Link>
                    </td>
                    <td className="text-muted">{item.ticker_name}</td>
                    <td><SignalCell s={item.signals} /></td>
                    <td><PatternCell s={item.signals} /></td>
                    <td><MomentumCell s={item.signals} /></td>
                    <td className="text-muted" style={{ fontSize: 13 }}>{item.created_at}</td>
                    <td className="text-end">
                      <Button
                        size="sm"
                        color="link"
                        style={{ color: "var(--error)", padding: "0 4px" }}
                        onClick={() => void removeTicker(item.ticker)}
                        title="Remove"
                      >
                        ✕
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </Container>
  );
}
